import { masterApis } from "stores/master/masterApis";
import { customerApis } from "stores/customer/customerApis";
import { customerParsers } from "stores/customer/customerParsers";
import { customerActions } from "stores/customer/customerActions";
import { validationHelper } from "helper/validation";
import {
  CKYC_DOC_STATUS,
  emptyCkycDocStatus,
  parseKycDocumentUpload,
  readNestedCkycDocStatusFromUpload,
} from "helper/kycDocumentUpload";

export const CKYC_PENDING_COPY = {
  title: "Your document is under review from our team",
  body: "You cannot re-upload until the team approves or rejects this document.",
};

export const CKYC_FAILED_COPY = {
  title: "We couldn't verify your document",
  body: "Please check that the name and document number match your PAN/Aadhaar.",
};

export const CKYC_REUPLOAD_LABEL = {
  panCard: "Re-upload PAN",
  aadharCardFront: "Re-upload Aadhaar Front",
  aadharCardBack: "Re-upload Aadhaar Back",
};

export const getCkycCustomerName = (loginProfile = {}) =>
  String(
    loginProfile.name ||
      loginProfile.fullName ||
      loginProfile.customerName ||
      "",
  ).trim();

// Navia iframe and PHC preview-link (Navia banner / OneMoney) users never get
// LOGIN_DATA, so "Name on record" is loaded from the profile API instead.
// Dispatches only when the name changed: a loginProfile update re-boots Intercom (App.js).
export const loadCkycCustomerName = (dispatch, currentName = "") =>
  customerApis
    .profile()
    .then((res) => {
      const fullName = String(customerParsers.profile(res).fullName || "").trim();
      if (fullName && fullName !== currentName) {
        dispatch(customerActions.updateFullName(fullName));
      }
    })
    .catch((err) => {
      console.log(err.message || "Error fetching profile");
    });

const toYmd = (date) => {
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return `${year}-${month}-${day}`;
};

export const formatCkycDateOfBirth = (value) => {
  if (!value) {
    return "";
  }

  if (value instanceof Date && !Number.isNaN(value.getTime())) {
    return toYmd(value);
  }

  const asString = String(value).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(asString)) {
    return asString.slice(0, 10);
  }

  const parsed = new Date(asString);
  if (!Number.isNaN(parsed.getTime())) {
    return toYmd(parsed);
  }

  return asString;
};

export const validateKycDocumentUpload = ({
  type,
  formData = {},
  customerName,
}) => {
  const errors = {};

  if (type === "panCard" || type === "aadharCardFront") {
    if (!String(customerName || "").trim()) {
      errors.customerName = "Name on record is missing";
    }
    const dob = validationHelper.dateOfBirth(formData.dateOfBirth);
    if (!dob.isValid) {
      errors.dateOfBirth = dob.message;
    }
  }

  if (type === "panCard") {
    const pan = validationHelper.pan(formData.panNumber);
    if (!pan.isValid) {
      errors.panNumber = pan.message;
    }
  }

  if (type === "aadharCardFront" || type === "aadharCardBack") {
    const aadhar = validationHelper.aadhar(formData.aadharNumber);
    if (!aadhar.isValid) {
      errors.aadharNumber = aadhar.message;
    }
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors,
  };
};

export const requestCkycDocumentVerify = ({
  type,
  file,
  fileName,
  originalFileName,
  productCode,
  loginProfile,
  formData,
}) => {
  const name = getCkycCustomerName(loginProfile);
  const dateOfBirth = formatCkycDateOfBirth(formData?.dateOfBirth);

  return masterApis.verifyKycDocument({
    [type]: file,
    [`${type}_fileName`]: fileName,
    originalFileName,
    productCode,
    name,
    panNumber: type === "panCard" ? formData?.panNumber : undefined,
    aadhaarNumber:
      type === "aadharCardFront" || type === "aadharCardBack"
        ? formData?.aadharNumber
        : undefined,
    dateOfBirth:
      type === "panCard" ||
      type === "aadharCardFront" ||
      type === "aadharCardBack"
        ? dateOfBirth
        : undefined,
  });
};

export const validateCkycCustomerName = (name) => {
  const trimmed = String(name || "").trim();
  if (!trimmed) {
    return "Name is required";
  }
  if (!/^[a-zA-Z\s]+$/.test(trimmed)) {
    return "Name can only contain letters and spaces";
  }
  if (trimmed.length < 3) {
    return "Name must be at least 3 characters";
  }
  return "";
};

export const areKycDocumentsReadyForSubmit = (
  _isImagesUploaded = {},
  _disableUpload = {},
  documentVerifyStatus,
) => {
  // Button should be enabled only when every document is actually APPROVED.
  // A document that is untouched/idle (or only "disabled" via cross-product
  // reuse) must NOT count as ready.
  const isReady = (docType) =>
    documentVerifyStatus?.[docType] === CKYC_DOC_STATUS.APPROVED;

  return (
    isReady("panCard") &&
    isReady("aadharCardFront") &&
    isReady("aadharCardBack")
  );
};

export const applyCkycUploadResult = (prev = {}, type, res) => {
  const parsed = parseKycDocumentUpload(res, type);
  return {
    formError: nextFormErrorAfterKycUpload(prev.formError, type, res),
    documentVerifyStatus: {
      ...(prev.documentVerifyStatus || emptyCkycDocStatus()),
      ...readNestedCkycDocStatusFromUpload(res),
      [type]: parsed.docStatus,
    },
  };
};

export const applyCkycUploadFailure = (prev = {}, type, message, error) => {
  const body = error?.response?.data;
  if (body && typeof body === "object") {
    const parsed = parseKycDocumentUpload(body, type);
    if (parsed.isPending || parsed.docStatus === CKYC_DOC_STATUS.PENDING) {
      return {
        formError: {
          ...(prev.formError || {}),
          [type]: "",
        },
        documentVerifyStatus: {
          ...(prev.documentVerifyStatus || emptyCkycDocStatus()),
          [type]: CKYC_DOC_STATUS.PENDING,
        },
      };
    }
    if (parsed.docStatus && parsed.docStatus !== CKYC_DOC_STATUS.FAILED) {
      return applyCkycUploadResult(prev, type, body);
    }
  }

  return {
    formError: {
      ...(prev.formError || {}),
      [type]: message,
    },
    documentVerifyStatus: {
      ...(prev.documentVerifyStatus || emptyCkycDocStatus()),
      [type]: CKYC_DOC_STATUS.FAILED,
    },
  };
};

export const isManualKycVerified = (res) => {
  if (!res) {
    return false;
  }
  const code = String(res.code || "").toUpperCase();
  if (code === "KYC009" || code === "JPHPSKYC001") {
    return true;
  }
  const body = res.result && typeof res.result === "object" ? res.result : {};
  return (
    body.isVerified === true || body.isVerified === 1 || body.isVerified === "1"
  );
};

export const getKycUploadRowError = (type, res) => {
  const parsed = parseKycDocumentUpload(res, type);
  if (parsed.isFailed) {
    return parsed.message || "Failed to verify";
  }
  return "";
};

export const nextFormErrorAfterKycUpload = (prevError = {}, type, res) => {
  const parsed = parseKycDocumentUpload(res, type);
  const next = {
    ...prevError,
    [type]: parsed.isFailed ? parsed.message || "Failed to verify" : "",
    customerName: "",
  };

  if (parsed.approved && type === "panCard") {
    next.panNumber = "";
    next.dateOfBirth = "";
  }

  if (
    parsed.approved &&
    (type === "aadharCardFront" || type === "aadharCardBack")
  ) {
    next.aadharNumber = "";
    if (type === "aadharCardFront") {
      next.dateOfBirth = "";
    }
  }

  return next;
};

export const getKycUploadCatchMessage = (error) =>
  error?.response?.data?.message || error?.message || "Failed to verify";
